import { assertNever } from '@sparkdotfi/common-universal'
import { UserCheckIcon } from 'lucide-react'
import { ReactNode } from 'react'

import { PointsType } from '@/features/points/types'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { links } from '@/ui/constants/links'
import { testIds } from '@/ui/utils/testIds'

interface EarningsDetailsProps {
  type: PointsType
  invitedUsers: number
}

export function EarningsDetails({ type, invitedUsers }: EarningsDetailsProps) {
  switch (type) {
    case 'pendle':
      return <PendleEarningsStepper />
    case 'referral':
      return (
        <div
          className="flex items-center justify-between md:justify-start md:gap-1.5"
          data-testid={testIds.points.earningPanel.details}
        >
          <div className="flex items-center gap-1">
            <UserCheckIcon className="icon-size-xxs" />
            <div className="typography-label-3 text-secondary">Invited users:</div>
          </div>
          <div className="typography-label-2 md:typography-label-3">{invitedUsers}</div>
        </div>
      )
    case 'staking':
      return null
    case 'hold-susdc':
    case 'hold-susds':
    case 'hold-spusdc':
    case 'hold-spusdt':
    case 'hold-speth':
      return null
    case 'governance':
      return (
        <div className="typography-label-4 flex flex-col gap-1 text-secondary">
          <div>Requirements:</div>
          <ol className="list-decimal space-y-1 pl-4 md:pl-5">
            <li>SPK or stSPK must be held in the wallet for 7 consecutive days.</li>
            <li>Must be actively delegated at the time of the governance snapshot.</li>
          </ol>
        </div>
      )
    case 'loyalty':
      return null
    default:
      assertNever(type)
  }
}

function PendleEarningsStepper() {
  return (
    <div className="mr-1 flex flex-col items-start gap-1" data-testid={testIds.points.earningPanel.details}>
      <EarningsStep index={1}>
        <div className="typography-label-4 mt-1">
          <span>Go to </span>
          <ExternalLink to={links.pendleWebsite} variant="underline" className="typography-label-4">
            Spark USDS Pendle pool
          </ExternalLink>
        </div>
      </EarningsStep>
      <EarningsStep index={2}>
        <div className="typography-label-4 mt-1">Buy USDS YT or LP on Pendle</div>
      </EarningsStep>
      <EarningsStep index={3} hasNextStep={false}>
        <div className="typography-label-4 mt-1">Your points will begin to accrue automatically</div>
      </EarningsStep>
    </div>
  )
}

function EarningsStep({
  index,
  children,
  hasNextStep = true,
}: {
  index: number
  children: ReactNode
  hasNextStep?: boolean
}) {
  return (
    <div key={index} className="grid grid-cols-[1fr_auto] content-center gap-2.5">
      <div className="flex flex-col items-center gap-1">
        <div className="typography-label-4 flex size-6 items-center justify-center rounded-full bg-tertiary">
          {index}
        </div>
        {hasNextStep && <div className="h-3 w-0.5 rounded bg-tertiary" />}
      </div>
      {children}
    </div>
  )
}
